import React from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, List, ListItemButton, ListItemText, Stack, Switch, Tab,
  Tabs, TextField, Typography,
} from '@mui/material';
import { useStargraveEquipment, useStargraveSoldiers } from '../../stores/stargraveCustomStore';
import { StargraveSoldier } from '../../types';

interface StargraveRecruitSoldierModalProps {
  open: boolean;
  availableCredits: number;
  onClose: () => void;
  onRecruit: (input: { profileId: string; name?: string; notes?: string; isRobot: boolean }) => void;
  initialSoldier?: StargraveSoldier;
  onEdit?: (input: { profileId: string; name?: string; notes?: string; isRobot: boolean }) => void;
}

export const StargraveRecruitSoldierModal: React.FC<StargraveRecruitSoldierModalProps> = ({
  open,
  availableCredits,
  onClose,
  onRecruit,
  initialSoldier,
  onEdit,
}) => {
  const [category, setCategory] = React.useState<'standard' | 'specialist' | 'creature'>('standard');
  const [profileId, setProfileId] = React.useState('');
  const [name, setName] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [isRobot, setIsRobot] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const stargraveEquipment = useStargraveEquipment();
  const stargraveSoldiers = useStargraveSoldiers();
  const profiles = stargraveSoldiers.filter((profile) => profile.category === category);
  const profile = stargraveSoldiers.find((candidate) => candidate.id === profileId);

  React.useEffect(() => {
    if (!open) return;
    if (initialSoldier) {
      const selectedProfile = stargraveSoldiers.find((candidate) => candidate.id === initialSoldier.profileId);
      setCategory(selectedProfile?.category ?? 'standard');
      setProfileId(initialSoldier.profileId);
      setName(initialSoldier.name);
      setNotes(initialSoldier.notes ?? '');
      setIsRobot(initialSoldier.isRobot);
      setError(null);
    }
  }, [open, initialSoldier]);

  const reset = () => {
    setCategory('standard');
    setProfileId('');
    setName('');
    setNotes('');
    setIsRobot(false);
    setError(null);
  };
  const handleClose = () => { reset(); onClose(); };
  const handleRecruit = () => {
    if (!profile) return setError('Select a soldier profile.');
    if (profile.cost > availableCredits) return setError('Not enough credits to recruit this soldier.');
    const input = { profileId: profile.id, name: name.trim() || undefined, notes: notes.trim() || undefined, isRobot };
    if (initialSoldier && onEdit) onEdit(input);
    else onRecruit(input);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialSoldier ? 'Edit Soldier' : 'Recruit Soldier'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography color="text.secondary">Available: {availableCredits}cr</Typography>
          <FormControlLabel control={<Switch checked={isRobot} onChange={(event) => setIsRobot(event.target.checked)} />} label="Robot" />
        </Stack>
        <Tabs value={category} onChange={(_, value) => { setCategory(value); setProfileId(''); }} variant="fullWidth">
          <Tab value="standard" label="Standard" />
          <Tab value="specialist" label="Specialists" />
          <Tab value="creature" label="Creatures" />
        </Tabs>
        <List dense sx={{ maxHeight: 280, overflowY: 'auto', border: 1, borderColor: 'divider' }}>
          {profiles.map((candidate) => (
            <ListItemButton key={candidate.id} selected={profileId === candidate.id}
              disabled={candidate.cost > availableCredits} onClick={() => { setProfileId(candidate.id); setError(null); }}>
              <ListItemText primary={candidate.name}
                secondary={`${candidate.cost === 0 ? 'Free' : `${candidate.cost}cr`} | M ${candidate.move} F +${candidate.fight} S +${candidate.shoot} A ${candidate.armour} W ${candidate.will >= 0 ? '+' : ''}${candidate.will} H ${candidate.health}`} />
            </ListItemButton>
          ))}
        </List>
        {profile && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6">{profile.name}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {profile.equipmentIds.map((id) => <Chip key={id} size="small" label={stargraveEquipment.find((item) => item.id === id)?.name ?? id} />)}
          </Box>
          {profile.notes && <Typography variant="body2" color="text.secondary">{profile.notes}</Typography>}
          <TextField label="Name (optional)" value={name} onChange={(event) => setName(event.target.value)} placeholder={profile.name} />
          <TextField label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={2} />
        </Box>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleRecruit} disabled={!profile}>{initialSoldier ? 'Save Changes' : 'Recruit'}</Button>
      </DialogActions>
    </Dialog>
  );
};
